import { useCallback, useMemo, useState } from 'react';
import { Button, InfoIcon } from 'tuui';
import { INDICATION_TYPES, Radio } from 'taboola-ultimate-ui';
import { RadioGroupField } from 'modules/campaigns/components/Form';
import OutlineEditableText from 'modules/campaigns/components/OutlineEditableText';
import { CROSS_SITE_BUTTON_TYPE } from 'modules/codeless-events/consts/conversionFlowConstants';
import { escapeEmojis, unescapeEmojis } from 'modules/codeless-events/utils/stringUtils';
import { buttonUrlValidations } from 'modules/codeless-events/validations/buttonValidations';
import { withIndication } from 'modules/errors';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue } from 'modules/taboola-common-frontend-modules/validations';
import { useValidationContext } from 'modules/taboola-common-frontend-modules/validations/ValidationContext';
import { STEP_IDS } from '../../config/stepIds';
import { NewEventHeader } from '../Headers/NewEventHeader';
import { CodelessCombineSameTextButtonsInputs } from './components/CodelessCombineSameTextButtonsInputs';
import styles from './globalButtonConversionSetup.module.scss';

const OutlineEditableTextWithIndication = withIndication(OutlineEditableText);

export const GlobalButtonConversionSetup = ({
    navigateToStepId,
    handleCancelWizard,
    updateDraftEvent,
    currentConversion,
}) => {
    const [setupType, setSetupType] = useState(currentConversion?.crossSiteButtonType);
    const [buttonTextErrors, setButtonTextErrors] = useState({});
    const { validationService } = useValidationContext();

    const initializeButtonTexts = () => {
        const trackedElements = currentConversion?.trackedElements || [];
        const texts = trackedElements
            .map(el => unescapeEmojis(el.displayText))
            .filter(text => text && text.trim())
            .map(text => text.trim());
        return texts.length > 0 ? texts : [''];
    };

    const [buttonTexts, setButtonTexts] = useState(initializeButtonTexts);
    const { formatMessage } = useIntl();

    const updateTrackedElementsFromTexts = useCallback(
        texts => {
            updateDraftEvent(draft => ({
                ...draft,
                trackedElements: texts
                    .filter(text => text.trim())
                    .map(text => ({
                        displayText: escapeEmojis(text.trim()),
                    })),
            }));
        },
        [updateDraftEvent]
    );

    const updateTrackedElementHref = useCallback(
        href => {
            updateDraftEvent(draft => {
                const existingElement = draft.trackedElements?.[0] || {};
                return {
                    ...draft,
                    trackedElements: [
                        {
                            ...existingElement,
                            href,
                        },
                    ],
                };
            });
        },
        [updateDraftEvent]
    );

    const resetTrackedElements = useCallback(() => {
        updateDraftEvent(draft => ({
            ...draft,
            trackedElements: [
                {
                    displayText: '',
                    href: '',
                },
            ],
        }));
    }, [updateDraftEvent]);

    const {
        value: buttonHref,
        onChange: onButtonHrefChange,
        indicationData: hrefIndicationData,
    } = useFormValidatedValue({
        field: 'trackedElements.0.href',
        validations: buttonUrlValidations,
        validationDependencies: {
            skipValidations: setupType !== CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL,
        },
    });

    const onRadioChange = useCallback(
        value => {
            setSetupType(value);
            setButtonTexts(['']);
            onButtonHrefChange('');
            updateDraftEvent(draft => ({
                ...draft,
                crossSiteButtonType: value,
            }));
            resetTrackedElements();
        },
        [resetTrackedElements, onButtonHrefChange, updateDraftEvent]
    );

    const handleButtonTextChange = useCallback(
        (value, index) => {
            const newTexts = [...buttonTexts];
            newTexts[index] = value;
            setButtonTexts(newTexts);
            updateTrackedElementsFromTexts(newTexts);
        },
        [buttonTexts, updateTrackedElementsFromTexts]
    );

    const addButtonText = useCallback(() => {
        setButtonTexts([...buttonTexts, '']);
    }, [buttonTexts]);

    const removeButtonText = useCallback(
        index => {
            if (buttonTexts.length > 1) {
                const newTexts = buttonTexts.filter((_, i) => i !== index);
                setButtonTexts(newTexts);
                updateTrackedElementsFromTexts(newTexts);
            }
        },
        [buttonTexts, updateTrackedElementsFromTexts]
    );

    const handleButtonHrefChange = useCallback(
        value => {
            onButtonHrefChange(value);
            updateTrackedElementHref(value);
        },
        [updateTrackedElementHref, onButtonHrefChange]
    );

    const navigateToPreviousStep = () => navigateToStepId(STEP_IDS.BUTTON_CONVERSION_LOCATION_SELECTOR);

    const handleButtonTextValidationChange = useCallback((index, hasError) => {
        setButtonTextErrors(prev => ({
            ...prev,
            [index]: hasError,
        }));
    }, []);

    const canNavigateToNext = () => {
        if (setupType === CROSS_SITE_BUTTON_TYPE.COMBINE_SAME_TEXT_BUTTONS) {
            const allTextsNonEmpty = buttonTexts.every(text => text && text.trim());
            const hasAnyErrors = Object.values(buttonTextErrors).some(hasError => hasError);
            return allTextsNonEmpty && !hasAnyErrors;
        } else if (setupType === CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL) {
            const hasUrlError = hrefIndicationData.indicationType === INDICATION_TYPES.ERROR;
            return !hasUrlError && buttonHref && buttonHref.trim();
        }
        return false;
    };

    const navigateToNextStep = async () => {
        const isValid = await validationService.validate();

        if (isValid && canNavigateToNext()) {
            navigateToStepId(STEP_IDS.SETUP_CLICKS_EVENT_CATEGORY);
        }
    };

    const optionsMap = useMemo(
        () => ({
            [CROSS_SITE_BUTTON_TYPE.COMBINE_SAME_TEXT_BUTTONS]: {
                title: (
                    <FormattedMessage
                        id="codeless.conversion.type.button.location.cross_site.same.text.title"
                        defaultMessage="Trigger event on all buttons that contain this text"
                    />
                ),
                placeholder: formatMessage({
                    id: 'codeless.conversion.type.button.location.cross_site.same.text.input.placeholder',
                    defaultMessage: 'E.g. submit now',
                }),
                values: buttonTexts,
                onChange: handleButtonTextChange,
                onAdd: addButtonText,
                onRemove: removeButtonText,
                isDisabled: setupType !== CROSS_SITE_BUTTON_TYPE.COMBINE_SAME_TEXT_BUTTONS,
                onValidationChange: handleButtonTextValidationChange,
            },
            [CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL]: {
                title: (
                    <FormattedMessage
                        id="codeless.conversion.type.button.location.cross_site.contains.url.title"
                        defaultMessage="Trigger event on all buttons that contain this URL"
                    />
                ),
                value: buttonHref,
                placeholder: formatMessage({
                    id: 'codeless.conversion.type.button.location.cross_site.contains.url.input.placeholder',
                    defaultMessage: 'E.g. /product-page',
                }),
                onChange: handleButtonHrefChange,
                isDisabled: setupType !== CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL,
                indicationData: hrefIndicationData,
            },
        }),
        [
            formatMessage,
            buttonTexts,
            setupType,
            buttonHref,
            handleButtonTextChange,
            handleButtonHrefChange,
            addButtonText,
            removeButtonText,
            hrefIndicationData,
            handleButtonTextValidationChange,
        ]
    );

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={navigateToPreviousStep} />

            <div className={styles['title-and-info-section']}>
                <div className={styles['title-section']}>
                    <FormattedMessage
                        id="codeless.conversion.global.setup.header.title"
                        defaultMessage="Track buttons across your site"
                    />
                </div>
                <div className={styles['info-box']}>
                    <InfoIcon className={styles['info-icon']} />
                    <div className={styles['info-text']}>
                        <FormattedMessage
                            id="codeless.global.button.setup.info"
                            defaultMessage="Matching buttons are highlighted on the page automatically."
                        />
                    </div>
                </div>
            </div>

            <RadioGroupField
                radioGroupName="conversionLocationSelection"
                onChange={onRadioChange}
                selectedValue={setupType}
                data-metrics-component="button-conversion-global-setup"
                className={styles['group']}
            >
                {Object.entries(optionsMap).map(([optionValue, optionData]) => (
                    <div key={`button-conversion-global-setup-${optionValue}`} className={styles['radio-container']}>
                        <div className={styles['radio-row']}>
                            <Radio
                                labelClassName={styles['radio']}
                                descriptionClassName={styles['description']}
                                aria-label={`${optionValue}`}
                                value={optionValue}
                                title={optionData.title}
                                description={optionData.description}
                                data-metrics-event-name={GTM_EVENTS.USABILITY}
                                data-metrics-value={optionValue}
                                inputClassName={styles['input']}
                            />
                        </div>
                        <div className={styles['input-container']}>
                            {optionValue === CROSS_SITE_BUTTON_TYPE.COMBINE_SAME_TEXT_BUTTONS ? (
                                <CodelessCombineSameTextButtonsInputs
                                    values={optionData.values}
                                    placeholder={optionData.placeholder}
                                    disabled={optionData.isDisabled}
                                    onChange={optionData.onChange}
                                    onAdd={optionData.onAdd}
                                    onRemove={optionData.onRemove}
                                    componentName={optionValue}
                                    onValidationChange={optionData.onValidationChange}
                                />
                            ) : (
                                <OutlineEditableTextWithIndication
                                    placeholder={optionData.placeholder}
                                    disabled={optionData.isDisabled}
                                    value={optionData.isDisabled ? optionData.placeholder : optionData.value}
                                    onChange={optionData.onChange}
                                    className={styles['editable-text']}
                                    data-metrics-event-name={GTM_EVENTS.USABILITY}
                                    data-metrics-component={`${optionValue}-input`}
                                    {...optionData.indicationData}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </RadioGroupField>

            <footer className={styles['footer']}>
                <Button
                    onClick={handleCancelWizard}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={navigateToNextStep}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </div>
    );
};
